import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { client } from '../client'; // Import Sanity client

// Helper function to map category to color
const getCategoryColor = (category) => {
  switch (category) {
    case 'PTA': return '#ecc94b'; // yellow
    case 'School Event': return '#805ad5'; // purple
    case 'Holiday': return '#e53e3e'; // red
    case 'Academic': return '#3182ce'; // blue
    case 'Parent Event': return '#38a169'; // green (Corrected from 'Parent Events')
    case 'Field Trip': return '#4fd1c5'; // teal
    case 'Meeting': return '#dd6b20'; // orange (Added)
    case 'Other': return '#718096'; // gray (Added)
    default: return '#718096'; // Default gray
  }
};

const CalendarPage = () => {
  // State for calendar events, modal, and loading
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Add error state

  // Fetch events from Sanity
  useEffect(() => {
    setLoading(true);
    setError(null); // Reset error on new fetch
    const query = `*[_type == "event"]{
      _id,
      title,
      startDateTime,
      endDateTime,
      allDay,
      category,
      description,
      location
    }`;

    client.fetch(query)
      .then((data) => {
        // Map Sanity data to FullCalendar event format
        const formattedEvents = data.map(event => {
          const color = getCategoryColor(event.category);
          return {
            id: event._id,
            title: event.title,
            start: event.startDateTime, // Use startDateTime directly
            end: event.endDateTime || null, // Use endDateTime, handle if null
            allDay: event.allDay || false, // Handle if null/undefined
            extendedProps: {
              category: event.category,
              description: event.description,
              location: event.location
            },
            backgroundColor: color,
            borderColor: color
          };
        });
        setEvents(formattedEvents);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching calendar events:', err);
        setError('Failed to load calendar events. Please try refreshing the page.');
        setLoading(false);
      });
  }, []); // Empty dependency array means this runs once on mount

  // Function to handle event click
  const handleEventClick = (clickInfo) => {
    setSelectedEvent({
      title: clickInfo.event.title,
      start: clickInfo.event.startStr, // Use startStr for display
      end: clickInfo.event.endStr, // Use endStr for display
      allDay: clickInfo.event.allDay,
      description: clickInfo.event.extendedProps.description,
      location: clickInfo.event.extendedProps.location,
      category: clickInfo.event.extendedProps.category,
      color: clickInfo.event.backgroundColor // Pass color for modal styling
    });
    setIsModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <div className="container mx-auto p-4 pt-28 md:pt-32 lg:pt-36"> {/* Added padding top for navbar */}
      <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">School Calendar</h1>

      {loading && <p className="text-center text-gray-600">Loading calendar...</p>}
      {error && <p className="text-center text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek,dayGridDay'
            }}
            events={events} // Use fetched events
            eventClick={handleEventClick}
            editable={false} // Set to false as we manage data via Sanity
            selectable={false} // No need to select dates if not adding events via UI
            dayMaxEvents={true} // Limits the number of events displayed per day, adding a "+n more" link
            // Add styling for the calendar container if needed
            height="auto" // Adjust height automatically
          />
        </div>
      )}

      {/* Event Detail Modal */}
      {isModalOpen && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg relative">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 text-2xl font-bold"
              aria-label="Close modal"
            >
              &times; {/* Unicode 'X' */}
            </button>

            {/* Event Title */}
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <span
                className="w-4 h-4 rounded-full mr-3 inline-block"
                style={{ backgroundColor: selectedEvent.color }} // Use passed color
                aria-hidden="true"
              ></span>
              {selectedEvent.title}
            </h2>

            {/* Event Details */}
            <div className="space-y-3 text-gray-700">
              {/* Date/Time */}
              <div>
                <strong className="font-medium text-gray-800">Date & Time:</strong>
                <p>
                  {selectedEvent.allDay
                    ? new Date(typeof selectedEvent.start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(selectedEvent.start) ? selectedEvent.start.replace(/-/g, '/') : selectedEvent.start).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                    : `${new Date(selectedEvent.start).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} ${selectedEvent.end ? `- ${new Date(selectedEvent.end).toLocaleString(undefined, { timeStyle: 'short' })}` : ''}`
                  }
                  {selectedEvent.allDay && selectedEvent.end && selectedEvent.start !== selectedEvent.end && ((
                    () => {
                      let endDateStr = selectedEvent.end;
                      if (typeof selectedEvent.end === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(selectedEvent.end)) {
                        endDateStr = selectedEvent.end.replace(/-/g, '/');
                      }
                      // Adjust end date display for multi-day all-day events
                      return ` - ${new Date(new Date(endDateStr).getTime() - 86400000).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;
                    }
                  )())}
                   {selectedEvent.allDay && ` (All Day)`}
                </p>
              </div>

              {/* Category */}
              {selectedEvent.category && (
                <div>
                  <strong className="font-medium text-gray-800">Category:</strong>
                  <p>{selectedEvent.category}</p>
                </div>
              )}

              {/* Location */}
              {selectedEvent.location && (
                <div>
                  <strong className="font-medium text-gray-800">Location:</strong>
                  <p>{selectedEvent.location}</p>
                </div>
              )}

              {/* Description */}
              {selectedEvent.description && (
                <div>
                  <strong className="font-medium text-gray-800">Description:</strong>
                  <p className="whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}
            </div>

            {/* Close Button (Footer) */}
            <div className="mt-6 text-right">
              <button
                onClick={closeModal}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage; 